import { useParams, useSearchParams } from 'react-router-dom'

import { useEffect, useState, useRef, useMemo } from 'react'

import { AnimatePresence, motion } from 'framer-motion'
import { goBack, onReady, postCTA, trackEvent, updateAppStatusBar } from '../../utils/appBridge'
import { EventNames } from '../../types/misc.types'
import { usePreRenderScales } from '../../hooks/usePreRenderScales'
import Button from '../../components/ui-components/Button'
import { TopBar } from '../../components/fd/best-plan/TopBar'
import { useQuery } from '@tanstack/react-query'
import { fetchInvestmentJourney } from '../../repository/FD'
import { FDInvestmentJourneyResponseType } from '../../types/fd-type'
import { AllSteps } from '../../components/fd/best-plan/AllSteps'
import { BestPlanSteps } from '../../types/fd-type'
import { formatTimePeriod, getFDFlowRedirectionCTA } from '../../utils/FD/fd'
import useWindowSize from '../../hooks/useWindowSize'
import { LoadingSpinner } from '../../components/LoadingSpinner'
import { getBottomPadding } from '../../utils/sessionStorage'

const FDBestPlanContent = ({ data, planId }: { data: FDInvestmentJourneyResponseType | undefined; planId: string }) => {
  const [currentStep, setCurrentStep] = useState<number>(0)
  const [direction, setDirection] = useState<1 | -1>(1)
  const [isButtonDisabled, setIsButtonDisabled] = useState(false)
  const [isNavigationDeferred, setIsNavigationDeferred] = useState(false)
  const [navigationStyle, setNavigationStyle] = useState('NONE')

  const [searchParams] = useSearchParams()
  const paramsIsNavigationDeferred = searchParams.get('isNavigationDeferred')
  const paramsNavigationStyle = searchParams.get('navigationStyle')

  useEffect(() => {
    if (paramsIsNavigationDeferred && paramsNavigationStyle) {
      setIsNavigationDeferred(paramsIsNavigationDeferred === 'true')
      setNavigationStyle(paramsNavigationStyle ?? 'NONE')
    }
  }, [paramsIsNavigationDeferred, paramsNavigationStyle])

  const bodyContainerRef = useRef<HTMLDivElement>(null)
  const parentContainerRef = useRef<HTMLDivElement>(null)

  const [currentInvestment, setCurrentInvestment] = useState(0)
  const [maturityAmount, setMaturityAmount] = useState(0)

  const { height } = useWindowSize()

  const handleInputRelatedAmounts = ({
    currInvestment,
    matureAmount
  }: {
    currInvestment: number
    matureAmount: number
  }) => {
    setCurrentInvestment(() => currInvestment)
    setMaturityAmount(() => matureAmount)
  }

  const TOTAL_STEPS = 10

  const ScreenName = 'FD_BEST_PLAN'

  const handleDisableButton = (isDisable: boolean) => {
    setIsButtonDisabled(() => isDisable)
  }

  const steps = [
    BestPlanSteps.FD_DETAILS_STEP,
    BestPlanSteps.INPUT_STEP,
    BestPlanSteps.INVESTMENT_SUMMARY_STEP,
    BestPlanSteps.PROGRESS_STEP
  ]

  const getStepId = (step: number) => {
    return steps?.[step]
  }

  useEffect(() => {
    setIsButtonDisabled(false)
  }, [currentStep])

  const currentStepId = useMemo(() => {
    return steps?.[currentStep]
  }, [currentStep])

  usePreRenderScales({
    totalSteps: TOTAL_STEPS,
    currentStep,
    parentContainerRef,
    renderContent: (step) => {
      if (height > 710) return null
      const stepId = getStepId(step)
      if (stepId === BestPlanSteps.INPUT_STEP) return null
      return (
        <AllSteps
          currentStepId={stepId}
          data={data}
          handleDisableButton={handleDisableButton}
          currentInvestment={currentInvestment}
          maturityAmount={maturityAmount}
          handleInputRelatedAmounts={handleInputRelatedAmounts}
        />
      )
    }
  })

  useEffect(() => {
    if (ScreenName)
      trackEvent({
        eventName: EventNames.ScreenLoaded,
        payload: { ScreenName, fundName: planId, screenType: currentStepId }
      })
  }, [ScreenName, currentStepId])

  useEffect(() => {
    if (isButtonDisabled && currentStepId === BestPlanSteps.INPUT_STEP) {
      trackEvent({
        eventName: EventNames.ButtonTap,
        payload: {
          ScreenName,
          fundName: planId,
          eventId: `${currentInvestment}`,
          buttonLabel: 'error_case',
          screenType: currentStepId
        }
      })
    }
  }, [isButtonDisabled, currentStepId])

  const handleLeftClick = () => {
    trackEvent({
      eventName: EventNames.ButtonTap,
      payload: {
        ScreenName,
        buttonLabel: 'back',
        fundName: planId,
        screenType: currentStepId
      }
    })
    setDirection(-1)
    setCurrentStep((prev) => {
      if (prev > 0) return prev - 1
      else {
        goBack()
        return prev
      }
    })
  }

  const handleRightClick = () => {
    let payload: { [key: string]: any } = {
      ScreenName,
      buttonLabel: 'continue',
      fundName: planId,
      screenType: currentStepId
    }
    if (currentStepId === BestPlanSteps.INPUT_STEP) payload.eventId = `${currentInvestment}`
    trackEvent({
      eventName: EventNames.ButtonTap,
      payload
    })
    setDirection(1)
    setCurrentStep((prev) => {
      if (prev < TOTAL_STEPS - 1) return prev + 1
      return prev
    })
  }

  const pageVariants = {
    enter: (direction: number) => ({ x: direction > 0 ? 300 : -300, opacity: 0 }),
    center: { x: 0, opacity: 1, transition: { duration: 0.1, ease: 'easeInOut' } },
    exit: (direction: number) => ({
      x: direction > 0 ? -300 : 300,
      opacity: 0,
      transition: { duration: 0.1, ease: 'easeInOut' }
    })
  }

  return (
    <div style={{ background: 'linear-gradient(180deg, #0767C5 0%, #119DE3 100%)' }} className='w-full h-screen'>
      <div className="flex flex-col h-full w-full bg-[url('/images/fd/bg-lines.svg')] bg-contain bg-no-repeat bg-blend-soft-light">
        <TopBar handleBack={handleLeftClick}>
          {[BestPlanSteps.FD_DETAILS_STEP, BestPlanSteps.INPUT_STEP]?.includes(currentStepId) && (
            <div className='text-[#D1ECFF] text-[16px] font-[400] animate-fade-550'>
              {data?.institutionName} @{data?.interestRate}% • {formatTimePeriod((data?.tenure ?? 0) / 365)}
            </div>
          )}
        </TopBar>
        <div
          id='parent-container'
          ref={parentContainerRef}
          className='flex flex-col flex-1 h-1 w-full justify-center items-center pb-24 px-5 relative z-0 text-white overflow-y-visible'
        >
          <div
            id='body-container'
            ref={bodyContainerRef}
            className='relative w-full h-full flex justify-center items-center transition-transform duration-300 ease-in-out'
          >
            <AnimatePresence custom={direction} mode='wait'>
              <motion.div
                key={currentStep}
                custom={direction}
                variants={pageVariants}
                initial='enter'
                animate='center'
                exit='exit'
                className='w-full h-full flex flex-col items-center'
              >
                <div className='w-full h-full flex flex-col items-center justify-center'>
                  <AllSteps
                    currentStepId={currentStepId}
                    data={data}
                    handleDisableButton={handleDisableButton}
                    currentInvestment={currentInvestment}
                    maturityAmount={maturityAmount}
                    handleInputRelatedAmounts={handleInputRelatedAmounts}
                  />
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
        <div style={{ paddingBottom: `calc(${getBottomPadding()}px + 20px)` }} className='px-5 pt-5 w-full relative'>
          {isButtonDisabled && (
            <div
              style={{ width: 'calc(100% - 40px)' }}
              className='absolute top-5 left-0 mx-5 rounded-xl h-[56px] !bg-[#4BB3F3]'
            ></div>
          )}
          <Button
            isFullWidth
            label={currentStepId === BestPlanSteps.INVESTMENT_SUMMARY_STEP ? 'Yes, Continue' : 'Continue'}
            buttonClassName={`!h-[56px] !text-black !text-[16px] ${isButtonDisabled ? 'opacity-50 !bg-[#4BB3F3]' : '!bg-white'}`}
            onClick={() => {
              if (!isButtonDisabled) {
                if (currentStepId === BestPlanSteps.PROGRESS_STEP) {
                  trackEvent({
                    eventName: EventNames.ButtonTap,
                    payload: {
                      ScreenName,
                      buttonLabel: 'continue',
                      screenType: currentStepId,
                      fundName: planId
                    }
                  })
                  postCTA(
                    JSON.stringify(
                      getFDFlowRedirectionCTA({
                        institutionId: data?.institutionId ?? '',
                        isNavigationDeferred: isNavigationDeferred,
                        navigationStyle: navigationStyle
                      })
                    )
                  )
                } else handleRightClick()
              }
            }}
          />
        </div>
      </div>
    </div>
  )
}

export const FDBestPlanPage = () => {
  const [planId, setPlanId] = useState('')
  const { planId: paramsPlanId } = useParams<{ planId: string }>()

  const { data: fdInvestmentJourneyFromQuery, isError } = useQuery<any, Error>({
    queryKey: ['mutualFundData', planId],
    queryFn: () => fetchInvestmentJourney(planId),
    enabled: !!planId,
    retry: 0,
    refetchOnWindowFocus: false
  })

  useEffect(() => {
    setPlanId('')
    setTimeout(() => {
      setPlanId(paramsPlanId ?? '')
    }, 0)
  }, [paramsPlanId])

  useEffect(() => {
    updateAppStatusBar('0767C5', 'light')
  }, [])

  useEffect(() => {
    if (fdInvestmentJourneyFromQuery?.data) {
      setTimeout(() => {
        onReady()
      }, 100)
    }
  }, [fdInvestmentJourneyFromQuery?.data])

  if (fdInvestmentJourneyFromQuery?.data)
    return <FDBestPlanContent data={fdInvestmentJourneyFromQuery?.data} planId={planId} />
  return (
    <div style={{ background: 'linear-gradient(180deg, #0767C5 0%, #119DE3 100%)' }} className='w-full h-screen'>
      <div className="flex flex-col h-full w-full bg-[url('/images/fd/bg-lines.svg')] bg-contain bg-no-repeat bg-blend-soft-light">
        <TopBar handleBack={goBack}></TopBar>
        <div className='flex flex-1 justify-center items-center w-full'>
          <div className='-mt-28 text-[14px] text-center font-[500] text-white'>
            {isError ? 'Something went wrong.' : <LoadingSpinner isWhite />}
          </div>
        </div>
      </div>
    </div>
  )
}
